const CURRENT_LEADERS = {
  'A.J.': {
    player: 'Drake Maye',
    rationale: 'Maye is No. 10 overall in the Aug. 19 ESPN superflex sheet at a Round 7 keeper cost. The six-round baseline discount grows in Bruins09 because passing touchdowns are worth six points and two quarterbacks must start.',
  },
  Amir: {
    player: 'Tyler Warren',
    rationale: 'Warren is No. 63 overall at a Round 9 cost, the only positive drafted-player discount on Amir’s current board. He left Aug. 19 practice with a groin issue, so this remains conditional on a clean medical update before the deadline.',
  },
  Anna: {
    player: 'Matthew Stafford',
    rationale: 'Stafford is No. 34 overall at a Round 11 cost—a seven-round baseline discount before Bruins09’s six-point passing-touchdown adjustment. That is the clearest drafted-player value on Anna’s roster.',
  },
  Bryan: {
    player: 'Justin Herbert',
    rationale: 'Herbert is No. 39 overall at a Round 6 cost in ESPN’s four-point passing-touchdown baseline. Bruins09 gives passing touchdowns six points and requires two starting quarterbacks, making his format-adjusted edge stronger than the raw two-round discount.',
  },
  Josh: {
    player: 'Quinshon Judkins',
    rationale: 'Judkins is No. 57 overall at a Round 14 cost—an eight-round baseline discount. That is substantially larger than Jonathan Taylor’s one-round discount and changes Josh’s stale May recommendation.',
  },
  Kevin: {
    player: 'Rashee Rice',
    rationale: 'Rice is No. 23 overall at a Round 10 cost—a seven-round baseline discount. His knee ramp-up remains the principal risk check, but the market-to-cost gap is still Kevin’s strongest drafted-player edge.',
  },
  Kyle: {
    player: 'Tyler Shough',
    rationale: 'Shough is the Saints’ unquestioned starter, No. 61 overall and QB17 on the Aug. 19 ESPN superflex sheet, and costs no 2026 pick. A free starting quarterback is the strongest structural advantage on Kyle’s board in a league that requires two QBs and awards six points per passing touchdown.',
  },
  Robby: {
    player: 'Colston Loveland',
    rationale: 'Loveland is No. 62 overall at a Round 15 cost—an eight-round baseline discount. That is materially better value than James Cook’s two-round discount and replaces the stale May leader.',
  },
  Ryan: {
    player: 'Brock Purdy',
    rationale: 'Purdy is No. 33 overall at a Round 8 cost in ESPN’s four-point passing-touchdown baseline. Travis Etienne has the larger raw round discount, but Bruins09’s mandatory second quarterback and six-point passing touchdowns move Purdy narrowly ahead on format-adjusted value.',
  },
  Samuel: {
    player: 'Jaxson Dart',
    rationale: 'Dart is No. 17 overall at a Round 14 cost—a twelve-round baseline discount before Bruins09’s quarterback-friendly scoring adjustment. This is the largest confirmed drafted-player edge on any current roster.',
  },
};

const roundForRank = (rank) => Math.ceil(rank / 10);
const signed = (value) => (value > 0 ? `+${value}` : String(value));

function currentCandidate(candidate, marketPlayer) {
  if (!marketPlayer) {
    return {
      ...candidate,
      market: 'Not in Aug. 19 overlay',
      surplus: 'n/a',
      grade: 'Verify roster',
      risk: 'Review news',
      note: 'This row is not present in the authenticated Yahoo final-offense roster plus ESPN market overlay. Verify current eligibility before using it.',
      surplusReasoning: 'No current market calculation is available for this row.',
      keeperImplication: 'Do not use this row for a final decision until its roster source is verified.',
    };
  }

  if (!marketPlayer.overallRank) {
    return {
      ...candidate,
      market: 'Outside ESPN top 300',
      surplus: 'n/a',
      grade: candidate.cost === 'No pick' ? 'No-pick flyer' : 'No market edge',
      risk: 'Review news',
      note: `${candidate.player} is confirmed on the authenticated Yahoo final roster but is outside ESPN’s Aug. 19 top 300.`,
      surplusReasoning: 'A reliable round surplus is not available from the current market source.',
      keeperImplication: candidate.cost === 'No pick'
        ? 'No draft pick is forfeited, but the current market source does not rank this player inside its top 300.'
        : 'Not a drafted-player value leader on the current evidence.',
    };
  }

  const marketRound = roundForRank(marketPlayer.overallRank);
  const noPickCost = candidate.cost === 'No pick';
  const costMatch = candidate.cost.match(/^Round (\d+)$/);
  const costRound = costMatch ? Number(costMatch[1]) : null;
  const surplus = costRound ? costRound - marketRound : null;
  const grade = noPickCost
    ? 'No-pick value'
    : surplus === null
      ? 'Verify cost'
    : surplus >= 6 ? 'Elite value'
      : surplus >= 3 ? 'Strong value'
        : surplus >= 1 ? 'Value'
          : surplus === 0 ? 'At market' : 'Above market';

  return {
    ...candidate,
    team: marketPlayer.nflTeam,
    market: `#${marketPlayer.overallRank} overall · ${marketPlayer.position}${marketPlayer.positionRank} · Round ${marketRound}`,
    surplus: noPickCost ? 'No pick' : surplus === null ? 'n/a' : signed(surplus),
    grade,
    risk: 'Review news',
    note: `Current market snapshot: ${candidate.player} is ${marketPlayer.position}${marketPlayer.positionRank} and No. ${marketPlayer.overallRank} overall on ESPN’s Aug. 19 PPR superflex sheet.`,
    surplusReasoning: noPickCost
      ? `Commissioner-confirmed no-pick keeper cost: ${candidate.player} may be kept without forfeiting a 2026 selection; current market value is Round ${marketRound}.`
      : surplus === null
        ? 'Keeper surplus cannot be calculated because the cost is unavailable.'
      : `Confirmed keeper cost Round ${costRound} minus current market Round ${marketRound} = ${signed(surplus)} rounds of baseline value.`,
    keeperImplication: noPickCost
      ? 'Valid no-pick candidate; compare the player’s projected contribution with the stronger drafted-player options before using the league’s single keeper slot.'
      : surplus >= 3
      ? 'Confirmed drafted-player value; compare the current medical/role risk before the deadline.'
      : 'Current market value alone does not create a large keeper advantage.',
  };
}

export function applyCurrentKeeperMarket(dossier, snapshot) {
  const marketManager = snapshot.managers.find((manager) => manager.manager === dossier.meta.manager);
  const leaderConfig = CURRENT_LEADERS[dossier.meta.manager];
  if (!marketManager || !leaderConfig) return dossier;

  const marketByPlayer = new Map(marketManager.players.map((player) => [player.player, player]));
  const candidates = dossier.candidates.map((candidate) => currentCandidate(candidate, marketByPlayer.get(candidate.player)));
  const rankedDrafted = candidates
    .filter((candidate) => /^Round \d+$/.test(candidate.cost) && /^[-+]?\d+$/.test(candidate.surplus))
    .sort((first, second) => Number(second.surplus) - Number(first.surplus));
  const leader = candidates.find((candidate) => candidate.player === leaderConfig.player);
  const alternative = rankedDrafted.find((candidate) => candidate.player !== leaderConfig.player);
  const marketLeader = marketByPlayer.get(leaderConfig.player);
  if (!leader || !marketLeader) return dossier;

  return {
    ...dossier,
    meta: {
      ...dossier.meta,
      status: 'current',
      asOfLabel: 'Aug 19, 2026',
      nextRefreshLabel: 'Aug 25, 2026',
      finalVerdictPublished: false,
    },
    summaryCards: [
      {
        iconKey: 'trophy',
        label: 'Current leader',
        value: leader.player,
        copy: leaderConfig.rationale,
      },
      {
        iconKey: 'key',
        label: 'Confirmed cost',
        value: leader.cost,
        copy: leader.cost === 'No pick'
          ? `${leader.market}. The commissioner-confirmed rule preserves every 2026 draft selection because ${leader.player} went undrafted in 2025.`
          : `${leader.market}. Baseline surplus: ${leader.surplus} rounds. Keeper cost comes from the authenticated 2025 Yahoo draft results.`,
      },
      {
        iconKey: 'chart',
        label: 'League adjustment',
        value: leader.position === 'QB' ? '2QB · 6PT TD' : '10-team PPR',
        copy: snapshot.leagueAdjustment,
      },
      {
        iconKey: 'shield',
        label: 'Confidence',
        value: 'Provisional',
        copy: 'Drafted-player and no-pick costs are confirmed. Final publication awaits the Aug. 25 injury, role, and market pass.',
      },
    ],
    decisionCards: [
      {
        label: 'Current recommendation',
        player: leader.player,
        tag: `${leader.cost} · ${leader.market}`,
        copy: leaderConfig.rationale,
      },
      alternative && {
        label: 'Baseline alternative',
        player: alternative.player,
        tag: `${alternative.cost} · ${alternative.market}`,
        copy: `${alternative.player} carries ${alternative.surplus} rounds of baseline market value. It remains behind the current recommendation after Bruins09 scoring and role risk are considered.`,
      },
      {
        label: 'Market source',
        player: 'ESPN Aug. 19',
        tag: 'PPR · 10 teams · superflex',
        copy: 'The current board uses ESPN’s Aug. 19, 2026 PPR superflex cheat sheet, then calls out the Bruins09 differences: two mandatory quarterbacks and six-point passing touchdowns.',
      },
      {
        label: 'No-pick ruling',
        player: 'Commissioner confirmed',
        tag: 'Undrafted in 2025 · no pick forfeited',
        copy: 'A player absent from the 2025 draft results may be kept without forfeiting a 2026 draft pick. Those players are now valid candidates on every board.',
      },
    ].filter(Boolean),
    boardLegend: {
      intro: 'Every candidate row now combines the authenticated Yahoo final roster and original 2025 draft round with a dated 2026 superflex market rank.',
      columns: [
        { label: 'Cost', copy: 'The original 2025 Yahoo draft round, or “No pick” when the player went undrafted.' },
        { label: 'Market', copy: 'ESPN’s Aug. 19 overall and positional rank, translated to a round in this 10-team league.' },
        { label: 'Surplus', copy: 'For drafted players, keeper cost round minus current market round. No-pick players are labeled separately because no selection is forfeited.' },
        { label: 'Grade', copy: 'A mechanical market-value tier. Medical, role, and league-format checks can still lower the final recommendation.' },
        { label: 'Risk', copy: 'Every row requires the Aug. 25 news pass; current leader caveats are called out above.' },
      ],
      formula: 'Surplus = keeper cost round − current market round.',
      example: leader.cost === 'No pick'
        ? `${leader.player}: ${leader.cost} versus ${leader.market}; every 2026 selection is preserved.`
        : `${leader.player}: ${leader.cost} versus ${leader.market}; baseline surplus ${leader.surplus} rounds.`,
    },
    candidates,
    sourceRequirements: [
      ['Yahoo roster source', 'Authenticated 2025 final rosters captured Aug. 19, 2026.', 'Live'],
      ['2025 draft cost', 'Authenticated Yahoo draft results; all 170 picks captured.', 'Live'],
      ['League scoring', 'Authenticated 2026 Yahoo settings, including 2QB and 6-point passing touchdowns.', 'Live'],
      ['Current market', 'ESPN PPR superflex cheat sheet updated Aug. 19, 2026.', 'Live'],
      ['Consensus check', 'FantasyPros superflex expert consensus updated Aug. 14, 2026.', 'Live'],
      ['Final news pass', 'Injuries and depth charts will be checked Aug. 25 before the deadline.', 'Pending'],
    ],
    evidencePack: [
      { iconKey: 'database', title: 'Yahoo source', items: ['10 final rosters', '170 draft picks', 'Original draft rounds', 'Confirmed no-pick rule'] },
      { iconKey: 'chart', title: 'Market source', items: ['ESPN Aug. 19', '10-team PPR', 'Superflex baseline', 'Top 300 parsed'] },
      { iconKey: 'shield', title: 'League fit', items: ['Two starting QBs', '6-point pass TDs', 'Full PPR', 'Keeper deadline Aug. 26'] },
      { iconKey: 'activity', title: 'Still pending', items: ['Aug. 25 market refresh', 'Aug. 25 injury pass', 'Final depth charts', 'Publishable verdict'] },
    ],
    weeklyRefresh: [
      { label: 'Aug. 19 rebuild', copy: 'Yahoo rosters, draft rounds, current market ranks, schedule, and historical data rebuilt.' },
      { label: 'News hotfix', copy: 'Material injury or role news can update the current leader before the final pass.' },
      { label: 'Aug. 25 final pass', copy: 'Refresh rankings and injuries one day before the keeper deadline.' },
    ],
    sourceLedger: [
      { type: 'adp', label: 'ESPN Aug. 19 superflex market', usedFor: 'Market round', why: 'Current 10-team PPR superflex baseline.', cadence: 'Aug. 19 and Aug. 25', status: 'live' },
      { type: 'consensus_rank', label: 'FantasyPros Aug. 14 superflex ECR', usedFor: 'Cross-check', why: 'Independent current expert consensus.', cadence: 'Aug. 25', status: 'live' },
      { type: 'projection_set', label: 'ESPN superflex values', usedFor: 'Relative market value', why: 'Current overall, positional, and auction-value baseline.', cadence: 'Aug. 25', status: 'live' },
      { type: 'player_news', label: 'Official NFL team camp reports', usedFor: 'Role and risk', why: 'No-pick leaders and alternatives were checked against current team depth charts and camp reporting.', cadence: 'Aug. 19 and Aug. 25', status: 'live' },
      { type: 'depth_chart', label: 'Current ESPN team/position data', usedFor: 'Role verification', why: 'Updates stale May team assignments.', cadence: 'Aug. 25', status: 'live' },
      { type: 'league_scoring', label: 'Yahoo 2026 settings', usedFor: 'Format adjustment', why: 'Authenticated league settings.', cadence: 'Static', status: 'live' },
    ],
    changeLog: [
      { date: 'Aug 19', title: 'Yahoo + market rebuild', copy: `Current leader: ${leader.player}. Roster and cost come from authenticated Yahoo evidence; market rank comes from ESPN’s Aug. 19 superflex sheet.` },
      { date: 'Aug 19', title: 'Commissioner rule confirmed', copy: 'Players not drafted in 2025 may be kept without forfeiting a 2026 draft pick.' },
      { date: 'Aug 25', title: 'Final deadline pass', copy: 'Pending: refresh market, injuries, and depth charts.' },
    ],
    changeNotes: [
      leaderConfig.rationale,
      'Any material injury, suspension, or role change before Aug. 26 triggers a re-rank.',
      'No-pick players are valid candidates; the Aug. 25 pass will compare them against each current leader before final publication.',
    ],
    dataCaveats: [
      'ESPN’s source uses one superflex slot and four-point passing touchdowns; Bruins09 requires two quarterbacks and uses six-point passing touchdowns, so quarterbacks receive an explicit upward format adjustment.',
      'Round surplus is a market baseline, not a projection guarantee. The final pass must include injuries and depth-chart changes.',
      'Yahoo does not encode the custom no-pick rule in its settings page; the commissioner confirmed it directly on Aug. 19, 2026.',
    ],
  };
}
